












































































































import { Component, Watch, Mixins } from 'vue-property-decorator';
import { Button, Input } from 'element-ui';
import AssignOperator from '@/components/shipment/AssignOperator.vue';
import Callout from '@/components/Callout.vue';
import ChannelSelect from '@/components/channel/ChannelSelect.vue';
import CopyShipment from '@/components/CopyShipment.vue';
import LockShipment from '@/components/shipment/LockShipment.vue';
import NavBar from '@/components/layout/NavBar.vue';
import NavBarNavigation from '@/components/layout/NavBarNavigation.vue';
import FollowShipment from '@/components/shipment/FollowShipment.vue';

// mixins/utils
import channels from '@/mixins/channels.ts';
import fromTo from '@/mixins/fromTo';
import { statusColor } from '@/utilities/textUtils';

// gql/service
import ChannelFragment from '@/graphql/fragments/ChannelFields.gql';
import fetchShipmentChannelListQuery from '@/graphql/queries/shipment/FetchShipmentChannelList.gql';
import updateShipment from '@/graphql/mutations/ShipmentUpdate.gql';

// types
import {
  InquiryStatusEnum,
  ShipmentFields,
  ShipmentStatusEnum,
  ChannelFields
} from '@/types/graphql/schemaTypes';
import { NavigationSection, CalloutOption } from '@/types/types';

@Component({
  components: {
    AssignOperator,
    Callout,
    ChannelSelect,
    CopyShipment,
    FollowShipment,
    LockShipment,
    NavBar,
    NavBarNavigation,
    'el-button': Button,
    'el-input': Input
  },
  apollo: {
    shipment: {
      query: fetchShipmentChannelListQuery,
      prefetch: true,
      variables() {
        return {
          shipmentArgs: {
            shipmentId: this.$route.params.id
          }
        };
      },
      update(data) {
        const shipment = data && data.shipments && data.shipments[0];

        if (shipment) {
          // reopen channel that are in open state
          this.reopenChannel(shipment.channelList);
          this.shipmentRef = shipment.trackedBlNumber;
          this.isLocked = shipment.locked;
        }
        return shipment;
      },
      fetchPolicy: 'cache-first'
    }
  }
})
export default class ShipmentMain extends Mixins(channels, fromTo) {
  private isModalVisible = false;
  private shipment: ShipmentFields | null = null;
  public shipmentRef = '';
  isLocked = false;
  public navigationSections: NavigationSection[] = [
    {
      i18nKey: 'shipment.navbar.activity',
      name: 'shipmentActivities'
    },
    {
      i18nKey: 'shipment.navbar.task',
      name: 'shipmentTasks'
    },
    {
      i18nKey: 'shipment.navbar.details',
      name: 'shipmentDetails'
    },
    {
      i18nKey: 'shipment.navbar.milestone',
      name: 'shipmentMilestones'
    },
    {
      i18nKey: 'shipment.navbar.invoice',
      name: 'shipmentInvoice'
    },
    {
      i18nKey: 'shared.file',
      name: 'shipmentAttachments'
    },
    {
      i18nKey: 'inquiry.navbar.details',
      name: 'shipmentOrderDetails'
    },
    {
      i18nKey: 'shipment.navbar.insurance',
      name: 'shipmentInsurance'
    },
    {
      i18nKey: 'shipment.navbar.naccs',
      name: 'shipmentNaccs'
    },
    {
      i18nKey: 'shipment.navbar.documents',
      name: 'shipmentDocuments'
    },
    {
      i18nKey: 'shared.labels.channel',
      name: 'shipmentChannels'
    }
  ];

  mounted() {
    this.$root.$on('channelCreated', this.fetchShipment);
  }

  destroyed() {
    this.$root.$off('channelCreated', this.fetchShipment);
  }

  private revertShipment() {
    if (this.shipment) {
      this.$store.dispatch('App/openConfirmationModal', {
        dispatch: 'Shipment/revertShipment',
        payload: {
          id: this.shipment.id,
          status: InquiryStatusEnum.inquiry_quoted
        },
        options: {
          titleLabel: `${this.$t('shipment.revert.modal-title')}`,
          modalContent: `${this.$t('shipment.revert.modal-content')}`
        }
      });
    }
  }

  private deleteShipment(): void {
    if (this.shipment) {
      this.$store.dispatch('App/openConfirmationModal', {
        dispatch: 'Shipment/deleteShipment',
        payload: this.shipment.id,
        options: {
          titleLabel: `${this.$t('shipment.confirm.archive-title')}`,
          modalContent: `${this.$t('shipment.confirm.archive-body')}`
        }
      });
    }
  }

  private unarchive() {
    if (this.shipment) {
      this.$store.dispatch('App/openConfirmationModal', {
        dispatch: 'Shipment/unarchiveShipment',
        payload: this.shipment.id,
        options: {
          titleLabel: `${this.$t('shipment.confirm.unarchive-title')}`
        }
      });
    }
  }

  private async completeShipment() {
    try {
      const response = await this.$apollo.mutate({
        mutation: updateShipment,
        variables: {
          shipmentUpdateArgs: {
            id: this.$route.params.id,
            status: ShipmentStatusEnum.shipment_completed
          }
        }
      });

      if (response) {
        this.$store.dispatch('Alert/addAlert', {
          type: 'success',
          message: this.$t('success.updated'),
          time: 2000
        });
      }
    } catch {
      this.$store.dispatch('Alert/addAlert', {
        type: 'error',
        message: this.$t('error.push'),
        time: 2000
      });
    } finally {
      this.fetchShipment();
    }
  }

  private statusColor(): string {
    return statusColor(this.shipment);
  }

  private fetchShipment() {
    this.$apollo.queries.shipment.refetch();
  }

  get calloutActions(): CalloutOption<string>[] {
    if (!this.shipment) return [];

    const calloutActions = [
      { label: 'shipment.complete', event: 'complete' },
      { label: 'shipment.revert.label', event: 'revert' }
    ];

    const archiveOrUnarchive =
      this.shipment.status === ShipmentStatusEnum.shipment_cancelled
        ? { label: 'shipment.unarchive', event: 'unarchive' }
        : { label: 'shipment.archive', event: 'delete' };

    return [...calloutActions, archiveOrUnarchive];
  }

  @Watch('$store.state.Shipment.action')
  private refresh(action: string) {
    if (action === 'revert') {
      this.$router.push({
        name: 'inquiryQuote',
        params: { id: this.shipment!.id }
      });
      this.$store.dispatch('Shipment/setAction', '');
    }
    if (action === 'delete') {
      this.$router.push({ name: 'shipments' });
      this.$store.dispatch('Shipment/setAction', '');
    }
  }

  public track() {
    window.open(
      `https://connect.track-trace.com/for/shippio/billoflading/${this.shipmentRef}/`
    );
  }
}
